import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Leaf, Shield, TrendingDown, MapPin } from 'lucide-react';
import { VALUE_TILES, MAT_PRODUCTS, SERVICE_INFO } from '../data/mock';

// Mat image URL from environment variable
const MAT_IMAGE_URL = process.env.REACT_APP_MAT_IMAGE_URL || '/images/mat-placeholder.png';

const iconMap = {
  Shield: Shield,
  TrendingDown: TrendingDown,
  Leaf: Leaf,
  MapPin: MapPin
};

const HomePage = () => {
  return (
    <div>
      {/* Hero Section - Clean, declarative */}
      <section className="section-padding-lg band-white">
        <div className="container">
          <div className="max-w-3xl">
            <h1 className="hero-large mb-6">
              Canada's Premium Mat Rental Program
            </h1>
            <p className="body-large mb-10 max-w-2xl">
              We offer a series of mats that are the best performing on the market, 
              providing business solutions for Safety, Health, and Environment.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" className="btn-primary">
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/custom-quote" className="btn-outline">
                Request Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Band - Card layout like ExecutiveMat */}
      <section className="section-padding band-gray">
        <div className="container">
          <div className="text-center mb-14">
            <h2 className="heading-1 mb-4">Our Services</h2>
            <p className="body-large max-w-2xl mx-auto">
              High-quality mats crafted for durability and safety, with nationwide service.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {MAT_PRODUCTS.map((product) => (
              <div key={product.id} className="card-clean text-center">
                <div className="w-full aspect-[4/3] bg-[var(--bg-secondary)] rounded-md mb-6 overflow-hidden">
                  <img 
                    src={MAT_IMAGE_URL} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="heading-3 mb-3">{product.name}</h3>
                <p className="body-small mb-4">{product.description}</p>
                <p className="mb-4">
                  <span className="text-2xl font-bold text-[var(--text-primary)]">${product.priceE4W.toFixed(2)}</span>
                  <span className="body-small text-[var(--text-secondary)]"> /4 weeks</span>
                </p>
                <Link to="/shop" className="text-link">
                  Select size
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps - Numbered like ExecutiveMat */}
      <section className="section-padding band-white">
        <div className="container">
          <div className="text-center mb-14">
            <h2 className="heading-1 mb-4">What to Expect</h2>
            <p className="body-large max-w-xl mx-auto">
              Professional Mat Services: Reliable Pickup, Thorough Cleaning, and Prompt Delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { num: 1, text: "All floor mats and supplies delivered on your scheduled delivery date." },
              { num: 2, text: "We exchange all soiled floor mats directly from your location." },
              { num: 3, text: "Products are cleaned and inspected to ensure quality assurance." },
              { num: 4, text: "Your entire order delivered on your next scheduled date." }
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="step-number mx-auto mb-5">{step.num}</div>
                <p className="body-regular">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Props - Clean cards */}
      <section className="section-padding band-gray">
        <div className="container">
          <div className="text-center mb-14">
            <h2 className="heading-1 mb-4">Why Executive Mat?</h2>
            <p className="body-large max-w-xl mx-auto">
              We deliver measurable value through superior products, transparent pricing, 
              and sustainable practices.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUE_TILES.map((tile) => {
              const IconComponent = iconMap[tile.icon];
              return (
                <div key={tile.id} className="card-clean">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-full bg-[var(--bg-secondary)] flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-[var(--brand-green)]" />
                    </div>
                    <div>
                      <h3 className="heading-3 mb-1">{tile.title}</h3>
                      <p className="body-small text-[var(--brand-green)] mb-4">{tile.subtitle}</p>
                      <ul className="space-y-2">
                        {tile.points.map((point, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-[var(--brand-green)] mt-0.5 flex-shrink-0" />
                            <span className="body-small">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sustainability Stats - Large numbers like ExecutiveMat */}
      <section className="section-padding band-white">
        <div className="container">
          <div className="text-center mb-14">
            <h2 className="heading-1 mb-4">Committed to Sustainability</h2>
            <p className="body-large max-w-2xl mx-auto">
              We are proud to be Canada's only ISO14001 (Environmental) and COR Safety Certified 
              mat rental company. Committed to tackling climate change head on, one mat at a time.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="stat-tile card-flat">
              <div className="stat-number">2.5M</div>
              <div className="stat-label">kg of CO2e Reduced</div>
            </div>
            <div className="stat-tile card-flat">
              <div className="stat-number">95%</div>
              <div className="stat-label">Carbon Intensity Reduction</div>
            </div>
            <div className="stat-tile card-flat">
              <div className="stat-number">100%</div>
              <div className="stat-label">Canadian Owned & Operated</div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Info Band */}
      <section className="py-10 band-gray border-y border-[var(--border-light)]">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="heading-3 mb-1">{SERVICE_INFO.billingFrequency}</p>
              <p className="body-small">Predictable billing</p>
            </div>
            <div>
              <p className="heading-3 mb-1">{SERVICE_INFO.serviceFrequency}</p>
              <p className="body-small">Professional service</p>
            </div>
            <div>
              <p className="heading-3 mb-1">{SERVICE_INFO.responseTime}</p>
              <p className="body-small">Delivery confirmation</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Clean, minimal */}
      <section className="section-padding band-white">
        <div className="container text-center">
          <h2 className="heading-1 mb-4">Get in Touch for More Information</h2>
          <p className="body-large max-w-xl mx-auto mb-10">
            We're here to assist you in finding the ideal solutions for your business.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/shop" className="btn-primary">
              Start Your Service
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/custom-quote" className="btn-outline">
              Request Custom Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
